<template>
  <div class="customer-sale-invoice">
    <div class="invoice-header">
      <h2>Facturations</h2>

      <!-- Barre de recherche -->
      <div class="invoice-search">
        <div class="search-input-wrapper">
          <i class="fa-solid fa-search"></i>
          <input 
            type="text" 
            placeholder="Rechercher par numéro de facture, nom ou téléphone du client..." 
            v-model="search"
            @input="handleSearch"
          />
          <button v-if="search" @click="clearSearch" class="clear-search-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <div class="flex justify-between items-center gap-2">
        <!-- Filtres de statut -->
        <div class="invoice-filters">
          <button :class="['filter-btn', { active: statusFilter === 'all' }]" @click="setStatusFilter('all')">
            Toutes
          </button>
          <button :class="['filter-btn', { active: statusFilter === 'pending' }]" @click="setStatusFilter('pending')">
            En attente
          </button>
          <button :class="['filter-btn', { active: statusFilter === 'partial' }]" @click="setStatusFilter('partial')">
            Partielles
          </button>
          <button :class="['filter-btn', { active: statusFilter === 'paid' }]" @click="setStatusFilter('paid')">
            Payées
          </button>
        </div>

        <!-- Filtres de date -->
        <div class="invoice-date-filters">
          <div class="date-filter-group">
            <label>Du</label>
            <input type="date" v-model="dateFrom" @change="handleDateFilterChange" />
          </div>
          <div class="date-filter-group">
            <label>Au</label>
            <input type="date" v-model="dateTo" @change="handleDateFilterChange" />
          </div>
          <button v-if="dateFrom || dateTo" @click="clearDateFilters" class="clear-date-btn">
            <i class="fa-solid fa-xmark"></i>
            Réinitialiser
          </button>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="invoice-loading">
      <div class="spinner-small"></div>
      <p>Chargement...</p>
    </div>

    <!-- Empty -->
    <div v-else-if="invoices.length === 0" class="no-invoices">
      <i class="fa-solid fa-file-invoice"></i>
      <h3>Aucune facture {{ getStatusFilterLabel() }}</h3>
    </div>

    <!-- Tableau -->
    <div v-if="invoices.length > 0" class="invoice-table-container">
      <table class="invoice-table">
        <thead>
          <tr>
            <th>N° Facture</th>
            <th>Date</th>
            <th>Échéance</th>
            <th>Montant</th>
            <th>Payé</th>
            <th>Reste</th>
            <th>Statut</th>
            <th class="th-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in invoices" :key="invoice.id">
            <td class="td-invoice-number">
              <span class="invoice-number" @click="viewInvoice(invoice)">{{ invoice.invoice_number }}</span>
            </td>
            <td class="td-date">{{ formatDate(invoice.created_at) }}</td>
            <td class="td-date">{{ invoice.due_date ? formatDate(invoice.due_date) : '-' }}</td>
            <td class="td-amount">{{ formatCurrency(invoice.total_amount) }}</td>
            <td class="td-amount paid">{{ formatCurrency(invoice.paid_amount) }}</td>
            <td class="td-amount remaining" :class="{ 'has-remaining': invoice.remaining_amount > 0 }">
              {{ formatCurrency(invoice.remaining_amount) }}
            </td>
            <td class="td-status">
              <span class="status-badge" :class="getStatusClass(invoice.status)">
                {{ getStatusLabel(invoice.status) }}
              </span>
            </td>
            <td class="td-actions">
              <div class="action-buttons">
                <button v-if="invoice.status !== 'paid'" @click.stop="openPaymentModal(invoice)" class="pay-btn"
                  title="Ajouter un paiement">
                  <i class="fa-solid fa-money-bill"></i>
                </button>
                <!-- Dropdown de téléchargement pour chaque facture -->
                <div class="invoice-download-dropdown-container">
                  <button @click.stop="toggleInvoiceDownloadDropdown(invoice.id)"
                    :disabled="invoice.downloading || invoice.downloadingDetailed" class="download-btn"
                    :class="{ active: openInvoiceDropdownId === invoice.id }" title="Télécharger la facture">
                    <i v-if="invoice.downloading || invoice.downloadingDetailed"
                      class="fa-solid fa-spinner fa-spin"></i>
                    <i v-else class="fa-solid fa-download"></i>
                  </button>
                  <div v-if="openInvoiceDropdownId === invoice.id" class="invoice-download-dropdown">
                    <button @click.stop="downloadInvoice(invoice)"
                      :disabled="invoice.downloading || invoice.downloadingDetailed" class="dropdown-item">
                      <i class="fa-solid fa-file-pdf"></i>
                      <span>Facture groupée</span>
                    </button>
                    <button @click.stop="downloadInvoiceDetailed(invoice)"
                      :disabled="invoice.downloading || invoice.downloadingDetailed" class="dropdown-item">
                      <i class="fa-solid fa-file-lines"></i>
                      <span>Facture détaillée</span>
                    </button>
                  </div>
                </div>
                <button @click.stop="viewInvoice(invoice)" class="view-btn" title="Voir détails">
                  <i class="fa-solid fa-eye"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <PaginationComponent v-if="!loading && invoices.length > 0" :current-page="currentPage" :per-page="perPage"
      :total="total" :to="paginationTo" :from="paginationFrom" @page-change="goToPage" />

    <!-- Modal de paiement -->
    <Teleport to="body">
      <div v-if="showPaymentModal" class="payment-modal-overlay" @click.self="closePaymentModal">
        <div class="payment-modal">
          <div class="payment-modal-header">
            <h3>Ajouter un paiement</h3>
            <button @click="closePaymentModal" class="close-btn">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div class="payment-modal-body">
            <div class="invoice-info">
              <p><strong>Facture:</strong> {{ selectedInvoice?.invoice_number }}</p>
              <p><strong>Reste à payer:</strong> {{ formatCurrency(selectedInvoice?.remaining_amount) }}</p>
            </div>
            <div class="form-group">
              <label>Montant</label>
              <input type="number" v-model.number="paymentForm.amount" :max="selectedInvoice?.remaining_amount" min="1"
                step="1" placeholder="Montant du paiement" />
            </div>
            <div class="form-group">
              <label>Type de paiement</label>
              <select v-model="paymentForm.payment_type">
                <option value="cash">Espèces</option>
                <option value="card">Carte bancaire</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="bank_transfer">Virement bancaire</option>
                <option value="check">Chèque</option>
              </select>
            </div>
            <div class="form-group">
              <label>Date du paiement</label>
              <input type="date" v-model="paymentForm.payment_date" />
            </div>
            <div class="form-group">
              <label>Notes (optionnel)</label>
              <textarea v-model="paymentForm.notes" rows="2" placeholder="Notes sur le paiement..."></textarea>
            </div>
          </div>
          <div class="payment-modal-footer">
            <button @click="closePaymentModal" class="cancel-btn" :disabled="isSubmittingPayment">
              Annuler
            </button>
            <button @click="submitPayment" class="submit-btn" :disabled="isSubmittingPayment || !isPaymentFormValid">
              <span v-if="isSubmittingPayment" class="spinner-small"></span>
              <span v-else>Enregistrer</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { saleInvoiceService } from '@/services';
import PaginationComponent from '@/components/common/PaginationComponent.vue';

export default defineComponent({
  name: 'CustomerSaleInvoice',
  components: {
    PaginationComponent
  },
  props: {
    customerId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const router = useRouter();

    const invoices = ref([]);
    const loading = ref(false);
    const statusFilter = ref('all');
    const dateFrom = ref('');
    const dateTo = ref('');
    const search = ref('');
    const searchTimeout = ref(null);

    // Pagination
    const currentPage = ref(1);
    const perPage = ref(10);
    const total = ref(0);
    const paginationFrom = ref(0);
    const paginationTo = ref(0);

    // Payment modal
    const showPaymentModal = ref(false);
    const selectedInvoice = ref(null);
    const isSubmittingPayment = ref(false);
    const paymentForm = ref({
      amount: null,
      payment_type: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });

    // Download dropdown individuel
    const openInvoiceDropdownId = ref(null);

    const isPaymentFormValid = computed(() => {
      return paymentForm.value.amount > 0 &&
        paymentForm.value.amount <= (selectedInvoice.value?.remaining_amount || 0) &&
        paymentForm.value.payment_type &&
        paymentForm.value.payment_date;
    });

    const loadInvoices = async () => {
      if (!props.customerId) return;

      try {
        loading.value = true;

        const params = {
          customer_id: props.customerId,
          page: currentPage.value,
          per_page: perPage.value
        };

        // Ajouter le filtre de recherche
        if (search.value && search.value.trim()) {
          params.search = search.value.trim();
        }

        if (statusFilter.value !== 'all') {
          params.status = statusFilter.value;
        }

        if (dateFrom.value) {
          params.date_from = dateFrom.value;
        }
        if (dateTo.value) {
          params.date_to = dateTo.value;
        }

        const response = await saleInvoiceService.getSaleInvoices(params);

        if (response.data.data) {
          invoices.value = (response.data.data || []).map(invoice => ({
            ...invoice,
            downloading: false,
            downloadingDetailed: false
          }));
        } else {
          invoices.value = [];
        }

        if (response.data.meta) {
          const meta = response.data.meta;
          total.value = meta.total || 0;
          perPage.value = meta.per_page || 10;
          paginationFrom.value = meta.from || 0;
          paginationTo.value = meta.to || 0;
        }
      } catch (error) {
        console.error('Erreur lors du chargement des factures:', error);
        invoices.value = [];
      } finally {
        loading.value = false;
      }
    };

    const setStatusFilter = (status) => {
      statusFilter.value = status;
      currentPage.value = 1;
      loadInvoices();
    };

    const handleDateFilterChange = () => {
      currentPage.value = 1;
      loadInvoices();
    };

    const clearDateFilters = () => {
      dateFrom.value = '';
      dateTo.value = '';
      currentPage.value = 1;
      loadInvoices();
    };

    const handleSearch = () => {
      // Délai pour éviter trop de requêtes pendant la saisie
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1; // Réinitialiser à la première page
        loadInvoices();
      }, 500);
    };

    const clearSearch = () => {
      search.value = '';
      currentPage.value = 1;
      loadInvoices();
    };

    const goToPage = (page) => {
      if (page < 1) return;
      currentPage.value = page;
      loadInvoices();
    };

    const getStatusFilterLabel = () => {
      const labels = {
        'all': '',
        'pending': 'en attente',
        'partial': 'partielle',
        'paid': 'payée'
      };
      return labels[statusFilter.value] || '';
    };

    const getStatusLabel = (status) => {
      const labels = {
        'pending': 'En attente',
        'partial': 'Partielle',
        'paid': 'Payée'
      };
      return labels[status] || status;
    };

    const getStatusClass = (status) => {
      const classes = {
        'pending': 'status-pending',
        'partial': 'status-partial',
        'paid': 'status-paid'
      };
      return classes[status] || '';
    };

    const viewInvoice = (invoice) => {
      router.push({ name: 'sale-invoice-details', params: { id: invoice.id } });
    };

    const toggleInvoiceDownloadDropdown = (invoiceId) => {
      if (openInvoiceDropdownId.value === invoiceId) {
        openInvoiceDropdownId.value = null;
      } else {
        openInvoiceDropdownId.value = invoiceId;
      }
    };

    const downloadInvoice = async (invoice) => {
      if (invoice.downloading) return;

      try {
        // Marquer l'invoice comme en cours de téléchargement
        invoice.downloading = true;
        // Fermer le dropdown
        openInvoiceDropdownId.value = null;

        await saleInvoiceService.downloadInvoice(invoice.id);
      } catch (error) {
        console.error('Erreur lors du téléchargement de la facture:', error);
        // Optionnel : afficher un message d'erreur à l'utilisateur
      } finally {
        invoice.downloading = false;
      }
    };

    const downloadInvoiceDetailed = async (invoice) => {
      if (invoice.downloadingDetailed) return;

      try {
        // Marquer l'invoice comme en cours de téléchargement
        invoice.downloadingDetailed = true;
        // Fermer le dropdown
        openInvoiceDropdownId.value = null;

        await saleInvoiceService.downloadInvoiceDetailed(invoice.id);
      } catch (error) {
        console.error('Erreur lors du téléchargement de la facture détaillée:', error);
        // Optionnel : afficher un message d'erreur à l'utilisateur
      } finally {
        invoice.downloadingDetailed = false;
      }
    };

    const openPaymentModal = (invoice) => {
      selectedInvoice.value = invoice;
      paymentForm.value = {
        amount: invoice.remaining_amount,
        payment_type: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
      };
      showPaymentModal.value = true;
    };

    const closePaymentModal = () => {
      showPaymentModal.value = false;
      selectedInvoice.value = null;
      paymentForm.value = {
        amount: null,
        payment_type: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
      };
    };

    const submitPayment = async () => {
      if (!selectedInvoice.value || !isPaymentFormValid.value) return;

      try {
        isSubmittingPayment.value = true;

        await saleInvoiceService.addPaymentToInvoice(selectedInvoice.value.id, {
          amount: paymentForm.value.amount,
          payment_type: paymentForm.value.payment_type,
          payment_date: paymentForm.value.payment_date,
          notes: paymentForm.value.notes || undefined
        });

        closePaymentModal();
        await loadInvoices();
        emit('refresh');
      } catch (error) {
        console.error('Erreur lors de l\'ajout du paiement:', error);
      } finally {
        isSubmittingPayment.value = false;
      }
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    const reload = () => {
      loadInvoices();
    };

    watch(() => props.customerId, () => {
      currentPage.value = 1;
      loadInvoices();
    });

    // Gérer le clic en dehors du dropdown pour le fermer
    const handleClickOutside = (event) => {
      const invoiceDropdown = event.target.closest('.invoice-download-dropdown-container');

      if (!invoiceDropdown && openInvoiceDropdownId.value !== null) {
        openInvoiceDropdownId.value = null;
      }
    };

    onMounted(() => {
      loadInvoices();
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      invoices,
      loading,
      statusFilter,
      dateFrom,
      dateTo,
      search,
      currentPage,
      perPage,
      total,
      paginationFrom,
      paginationTo,
      showPaymentModal,
      selectedInvoice,
      isSubmittingPayment,
      paymentForm,
      isPaymentFormValid,
      setStatusFilter,
      handleDateFilterChange,
      clearDateFilters,
      handleSearch,
      clearSearch,
      goToPage,
      getStatusFilterLabel,
      getStatusLabel,
      getStatusClass,
      viewInvoice,
      downloadInvoice,
      downloadInvoiceDetailed,
      openInvoiceDropdownId,
      toggleInvoiceDownloadDropdown,
      openPaymentModal,
      closePaymentModal,
      submitPayment,
      formatCurrency,
      formatDate,
      reload
    };
  }
});
</script>

<style scoped>
.customer-sale-invoice {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.invoice-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.invoice-header h2 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.invoice-search {
  width: 100%;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-input-wrapper .fa-search {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  pointer-events: none;
}

.search-input-wrapper input {
  width: 100%;
  padding: 0.625rem 2.5rem 0.625rem 2.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s;
}

.search-input-wrapper input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.clear-search-btn:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: white;
  color: #111827;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover:not(:disabled) {
  background: #f9fafb;
}

.dropdown-item:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dropdown-item i {
  width: 16px;
  color: #0A5C6B;
}

.invoice-download-dropdown-container {
  position: relative;
}

.invoice-download-dropdown {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 180px;
  z-index: 100;
  overflow: hidden;
}

.download-btn.active {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.invoice-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: #f9fafb;
}

.filter-btn.active {
  background: #0A5C6B;
  color: white;
  border-color: #0A5C6B;
}

.invoice-date-filters {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.date-filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.date-filter-group input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  color: #111827;
  transition: all 0.2s;
}

.date-filter-group input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.clear-date-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-date-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #111827;
}

.invoice-loading,
.no-invoices {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #6b7280;
}

.no-invoices h3 {
  margin: 0.5rem 0 0;
  font-size: 1rem;
  font-weight: 500;
}

/* Table styles */
.invoice-table-container {
  overflow-x: auto;
}

.invoice-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.invoice-table thead {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.invoice-table th {
  padding: 0.875rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.invoice-table th.th-actions {
  width: 100px;
  text-align: center;
}

.invoice-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s;
}

.invoice-table tbody tr:hover {
  background: #f8fafc;
}

.invoice-table td {
  padding: 1rem;
  color: #334155;
  vertical-align: middle;
}

.td-invoice-number .invoice-number {
  font-weight: 600;
  color: #0A5C6B;
  cursor: pointer;
  transition: color 0.15s;
}

.td-invoice-number .invoice-number:hover {
  color: #094854;
  text-decoration: underline;
}

.td-date {
  color: #64748b;
  white-space: nowrap;
}

.td-amount {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
}

.td-amount.paid {
  color: #059669;
}

.td-amount.remaining {
  color: #64748b;
}

.td-amount.remaining.has-remaining {
  color: #dc2626;
}

.td-status {
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-partial {
  background: #dbeafe;
  color: #1e40af;
}

.status-paid {
  background: #d1fae5;
  color: #065f46;
}

.td-actions {
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.pay-btn,
.view-btn,
.download-btn,
.download-detailed-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.pay-btn {
  color: #059669;
  border-color: #d1fae5;
}

.pay-btn:hover {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.download-btn {
  color: #0A5C6B;
  border-color: #dbeafe;
}

.download-btn:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.download-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.download-detailed-btn {
  color: #7c3aed;
  border-color: #ede9fe;
}

.download-detailed-btn:hover:not(:disabled) {
  background: #f5f3ff;
  border-color: #ddd6fe;
}

.download-detailed-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.view-btn {
  color: #6b7280;
  border-color: #e5e7eb;
}

.view-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

/* Payment Modal */
.payment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.payment-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.payment-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.payment-modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.payment-modal-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.invoice-info {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
}

.invoice-info p {
  margin: 0;
  font-size: 0.875rem;
  color: #475569;
}

.invoice-info p+p {
  margin-top: 0.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.625rem 0.875rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  color: #111827;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 60px;
}

.payment-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn,
.submit-btn {
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.cancel-btn {
  background: white;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}

.cancel-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.submit-btn {
  background: #0A5C6B;
  border: none;
  color: white;
  min-width: 120px;
}

.submit-btn:hover:not(:disabled) {
  background: #094854;
}

.submit-btn:disabled,
.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .invoice-header {
    flex-direction: column;
    gap: 1rem;
  }

  .invoice-table th,
  .invoice-table td {
    padding: 0.75rem 0.5rem;
  }

  .td-invoice-number .invoice-number {
    font-size: 0.8rem;
  }

  .payment-modal {
    max-width: 100%;
    margin: 1rem;
  }
}
</style>
